import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { EntityRecord } from '@/types/entity';
import { useEntityOwnership, type TUseEntityOwnershipReturn } from '@/react/hooks/entityHooks/useEntityOwnership';
import { EXCLUDE_NOTIFY_TYPE_BY_ENTITY_TYPE } from '../constants/ownershipConstants';
import { Task } from '@/bryntum-lib/models/Task';
import { Resource } from '@/bryntum-lib/models/Resource';
import { Client } from '@/bryntum-lib/models/Client';

interface EntityOwnershipContextValue extends TUseEntityOwnershipReturn {
  entityRecord: EntityRecord;
  entityName: string;
  excludeTypes: string[];
}

const EntityOwnershipContext = createContext<EntityOwnershipContextValue | undefined>(undefined);

interface EntityOwnershipProviderProps {
  children: ReactNode;
  record: EntityRecord;
}

export const EntityOwnershipProvider: React.FC<EntityOwnershipProviderProps> = ({ children, record: entityRecord }) => {

  const entityType = entityRecord.entityType;

  // Determine entity name based on instance type
  const entityName = useMemo(() => {
    if (entityRecord instanceof Task) return "task";
    if (entityRecord instanceof Resource) return "resource";
    if (entityRecord instanceof Client) return "client";
    return "entity"; // fallback
  }, [entityRecord]);

  // Get excluded types for this entity
  const excludeTypes = EXCLUDE_NOTIFY_TYPE_BY_ENTITY_TYPE[entityType] || [];

  const ownershipOperations = useEntityOwnership({
    record: entityRecord,
    excludeTypes
  });

  const contextValue = useMemo(
    () => ({
      ...ownershipOperations,
      entityRecord,
      entityName,
      excludeTypes
    }),
    [ownershipOperations, entityRecord, entityName, excludeTypes]
  );

  return (
    <EntityOwnershipContext.Provider value={contextValue}>
      {children}
    </EntityOwnershipContext.Provider>
  );
};

export const useEntityOwnershipContext = (): EntityOwnershipContextValue => {
  const context = useContext(EntityOwnershipContext);
  if (!context) {
    throw new Error('useEntityOwnershipContext must be used within EntityOwnershipProvider');
  }
  return context;
};